import { useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { api, ApiError, buildQueryString } from "@/lib/api";
import { Download, Hourglass, Loader2, Lock, Trash2, X } from "lucide-react";
import { IconFolder, IconCoin } from "@/components/ui/cartoon-icons";
import { VideoWithLoader } from "@/components/ui/video-loader";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { hasExpiredFiles, RETENTION_LABEL } from "@/config/retention";
import { createPortal } from "react-dom";
import type { Video } from "@/types";

const PAGE_SIZE = 50;

export function AssetsPage() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Video | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/videos/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      setDeleteTarget(null);
    },
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["videos", user?.id],
    queryFn: async ({ pageParam }) => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ limit: PAGE_SIZE, offset: pageParam })}`,
      );
    },
    enabled: !!user?.id,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage.videos?.length ?? 0) < PAGE_SIZE
        ? undefined
        : allPages.reduce((sum, page) => sum + (page.videos?.length ?? 0), 0),
  });

  const videos = data?.pages.flatMap((page) => page.videos) ?? [];
  // Expired runs keep their row but their files are gone, so they don't count.
  const liveVideos = videos.filter((video) => !hasExpiredFiles(video));
  const hasDownloadable = liveVideos.some((video) => video.status === "completed" && video.url);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <div className="mb-8 animate-fade-in-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-[#241d3a] tracking-tight mb-1">Assets</h1>
          <p className="text-sm text-[#241d3a]/60">
            {videos.length > 0
              ? `${liveVideos.length}${hasNextPage ? "+" : ""} creation${liveVideos.length === 1 ? "" : "s"}`
              : "Your characters, videos, and images will appear here"}
          </p>
        </div>
        {hasDownloadable && <DownloadAllButton />}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-6 animate-spin text-[#241d3a]/55" />
        </div>
      )}

      {!isLoading && videos.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="size-16 rounded-3xl bg-[#241d3a]/[0.06] border border-[#241d3a]/15 flex items-center justify-center mb-4">
            <IconFolder className="size-10" />
          </div>
          <h3 className="text-base font-bold text-[#241d3a] mb-1.5">No creations yet</h3>
          <p className="text-sm text-[#241d3a]/60 mb-6">Generate your first video to get started</p>
          <Link to="/create-cartoon" className="btn-accent text-sm">Generate Video</Link>
        </div>
      )}

      {videos.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 animate-fade-in delay-100">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} onClick={() => setSelected(video)} onDelete={() => setDeleteTarget(video.id)} />
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="btn-glass px-6 py-2.5 text-[13px] inline-flex items-center gap-2 disabled:opacity-60"
              >
                {isFetchingNextPage && <Loader2 className="size-3.5 animate-spin" />}
                {isFetchingNextPage ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {selected && <AssetModal video={selected} onClose={() => setSelected(null)} />}
      {deleteTarget && (
        <DeleteConfirmModal
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

/**
 * Asks the API for a short-lived signed link (it needs the Clerk token, which a
 * plain link can't carry), then hands it to the browser so the zip streams
 * straight to disk.
 */
function DownloadAllButton() {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [excluded, setExcluded] = useState(0);

  const handleClick = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const res = await api.post<{ download_url: string; included: number; excluded: number }>("/library/export");
      setExcluded(res.excluded);
      window.location.assign(res.download_url);
    } catch (err) {
      setError(err instanceof ApiError ? err.errorMessage : "Couldn't start the download. Please try again.");
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap justify-end">
      {error ? (
        <p className="text-[12px] text-red-400 max-w-xs">{error}</p>
      ) : excluded > 0 ? (
        <p className="text-[12px] text-[#241d3a]/55 max-w-xs">
          {excluded} item{excluded === 1 ? "" : "s"} need a token pack and weren't included.
        </p>
      ) : null}
      <button
        onClick={handleClick}
        disabled={isStarting}
        className="btn-glass flex items-center gap-2 text-[13px] py-2.5 px-5 rounded-xl disabled:opacity-60"
      >
        {isStarting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
        Download all
      </button>
    </div>
  );
}

function VideoCard({ video, onClick, onDelete }: { video: Video; onClick: () => void; onDelete: () => void }) {
  const statusConfig: Record<string, { color: string; bg: string }> = {
    completed: { color: "text-emerald-400", bg: "bg-emerald-500/10" },
    processing: { color: "text-[#241d3a]/60", bg: "bg-[#241d3a]/[0.06]" },
    pending: { color: "text-[#241d3a]/60", bg: "bg-[#241d3a]/[0.06]" },
    failed: { color: "text-red-400", bg: "bg-red-500/10" },
  };
  const sc = statusConfig[video.status] || statusConfig.pending;
  const expired = hasExpiredFiles(video);
  const viewable = !expired && video.status === "completed" && !!video.url;

  return (
    <div
      onClick={viewable ? onClick : undefined}
      className={cn(
        "group rounded-2xl border border-[#241d3a]/10 bg-[#241d3a]/[0.04] overflow-hidden hover:border-[#241d3a]/15 transition-all",
        viewable && "cursor-pointer card-hover",
      )}
    >
      {viewable && video.url ? (
        <div className="relative w-full aspect-square bg-[#ece7f8] overflow-hidden">
          {video.media_type === "IMAGE" ? (
            <img src={video.url} alt={video.prompt} className="w-full h-full object-cover" />
          ) : (
            <VideoWithLoader src={`${video.url}#t=0.1`} className="w-full h-full object-cover" muted loop playsInline preload="metadata"
              onPointerEnter={(e) => (e.target as HTMLVideoElement).play().catch(() => {})}
              onPointerLeave={(e) => { (e.target as HTMLVideoElement).pause(); (e.target as HTMLVideoElement).currentTime = 0; }}
            />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="absolute top-2 right-2 p-1.5 rounded-xl bg-black/40 backdrop-blur-sm text-white/60 hover:text-red-400 border border-white/10 hover:bg-red-500/20 transition-all md:opacity-0 md:group-hover:opacity-100"
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      ) : (
        <div className="relative w-full aspect-square bg-[#ece7f8] flex items-center justify-center">
          {expired ? (
            // Past the retention window: the files are deleted, so request nothing.
            <div className="flex flex-col items-center gap-1.5 text-center px-3">
              <Hourglass className="size-5 text-[#241d3a]/40" />
              <span className="text-[11px] font-bold text-[#241d3a]/60">Expired</span>
              <span className="text-[10px] text-[#241d3a]/45">Deleted after {RETENTION_LABEL}</span>
            </div>
          ) : video.status === "failed" ? (
            <span className="text-[10px] text-red-400">Failed</span>
          ) : video.locked ? (
            <Link
              to="/billing?src=assets_locked"
              onClick={(e) => e.stopPropagation()}
              className="flex flex-col items-center gap-1.5 text-[10px] font-bold text-[#7c5cf0] hover:underline"
            >
              <Lock className="size-5" />
              Unlock with a token pack
            </Link>
          ) : (
            <Loader2 className="size-5 animate-spin text-[#241d3a]/55" />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="absolute top-2 right-2 p-1.5 rounded-xl bg-black/40 backdrop-blur-sm text-white/60 hover:text-red-400 border border-white/10 transition-all md:opacity-0 md:group-hover:opacity-100"
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      )}
      <div className="px-2.5 py-2">
        <p className="text-[10px] sm:text-[11px] text-[#241d3a]/60 truncate mb-1">{video.prompt}</p>
        <div className="flex items-center justify-between">
          {expired ? (
            <span className="text-[9px] py-0.5 text-[#241d3a]/55 tabular-nums">{new Date(video.created_at).toLocaleDateString()}</span>
          ) : (
            <span className={cn("text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full", sc.color, sc.bg)}>
              {video.status}
            </span>
          )}
          <span className="text-[9px] text-[#241d3a]/55 tabular-nums inline-flex items-center gap-0.5"><IconCoin className="size-2.5" />{video.credits_used}</span>
        </div>
      </div>
    </div>
  );
}

function AssetModal({ video, onClose }: { video: Video; onClose: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px)" }}>
      <div className="fixed inset-0 bg-[#241d3a]/35 backdrop-blur-sm" onClick={onClose} style={{ zIndex: 0 }} />
      <div className="relative w-full max-w-2xl mx-4 rounded-3xl overflow-hidden border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] bg-white animate-modal-in flex flex-col" style={{ zIndex: 1, maxHeight: "80vh" }}>
        <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 rounded-xl bg-black/40 backdrop-blur-sm text-white/70 hover:text-white border border-white/20 transition-colors">
          <X className="size-4" />
        </button>
        {video.url && (
          <div className="flex-1 min-h-0 bg-[#ece7f8] flex items-center justify-center">
            {video.media_type === "IMAGE" ? (
              <img src={video.url} alt={video.prompt} className="max-w-full max-h-[calc(80vh-80px)] object-contain" />
            ) : (
              <VideoWithLoader src={video.url} wrapperClassName="flex items-center justify-center" className="max-w-full max-h-[calc(80vh-80px)] object-contain" controls autoPlay loop playsInline />
            )}
          </div>
        )}
        <div className="shrink-0 p-4">
          <p className="text-[12px] text-[#241d3a]/60 leading-relaxed mb-3">{video.prompt}</p>
          <div className="flex items-center gap-3 text-[10px] text-[#241d3a]/55">
            <span className="inline-flex items-center gap-0.5"><IconCoin className="size-2.5" />{video.credits_used}</span>
            <span>{video.media_type === "IMAGE" ? "Image" : "Video"}</span>
            <span>{new Date(video.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DeleteConfirmModal({ isPending, onConfirm, onCancel }: { isPending: boolean; onConfirm: () => void; onCancel: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px)" }}>
      <div className="fixed inset-0 bg-[#241d3a]/35 backdrop-blur-sm" onClick={onCancel} style={{ zIndex: 0 }} />
      <div className="relative w-full max-w-sm mx-4 rounded-3xl border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] bg-white p-6 animate-modal-in text-center" style={{ zIndex: 1 }}>
        <div className="size-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="size-5 text-red-400" />
        </div>
        <h3 className="text-[15px] font-bold text-[#241d3a] mb-1">Delete Creation</h3>
        <p className="text-xs text-[#241d3a]/60 mb-5">This action cannot be undone.</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-glass flex-1 py-2.5 text-[13px]">Cancel</button>
          <button onClick={onConfirm} disabled={isPending}
            className="flex-1 py-2.5 rounded-2xl bg-red-50 border border-red-200/50 text-red-400 text-[13px] font-bold hover:bg-red-100 transition-colors flex items-center justify-center gap-2">
            {isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
